<template>
  <section class="map-section">
    <div class="map-section__layout">
      <div class="map-section__main">
        <div class="map-section__header reveal-child" :style="delay(0.1)">
          <div class="map-section__title-block">
            <h2 class="map-section__title">知识地图 - {{ selectedProvince }}</h2>
            <p class="map-section__subtitle">ZHI SHI DI TU</p>
          </div>
          <div class="map-section__stats">
            <div v-for="stat in stats" :key="stat.label" class="map-section__stat">
              <span class="map-section__stat-label">{{ stat.label }}</span>
              <span class="map-section__stat-value">{{ stat.value }}</span>
            </div>
          </div>
        </div>

        <div class="map-section__chart reveal-child" :style="delay(0.25)">
          <div class="map-section__chart-frame">
            <div ref="chartRef" class="map-section__chart-canvas" />
          </div>
          <p class="map-section__map-credit">地图数据来源：国家地理信息公共服务平台 · 天地图</p>
        </div>
      </div>

      <aside class="map-section__sidebar reveal-child" :style="delay(0.4)">
        <div class="map-sidebar">
          <!-- <div class="map-sidebar__modes">
            <span class="map-sidebar__modes-label">地图模式</span>
            <div class="map-sidebar__mode-tabs">
              <button
                v-for="mode in mapModes"
                :key="mode.value"
                type="button"
                class="map-sidebar__mode-tab"
                :class="{ 'map-sidebar__mode-tab--active': mapMode === mode.value }"
                @click="mapMode = mode.value"
              >
                {{ mode.label }}
              </button>
            </div>
          </div> -->

          <div class="map-sidebar__context">
            <span>知识地图：{{ selectedProvince }}</span>
            <button
              type="button"
              class="map-sidebar__reset"
              :disabled="selectedProvince === '全国'"
              @click="handleReset"
            >
              重置
            </button>
          </div>

          <div class="map-sidebar__filters">
            <button
              v-for="filter in filters"
              :key="filter.value"
              type="button"
              class="map-sidebar__filter"
              :class="{ 'map-sidebar__filter--active': activeFilter === filter.value }"
              @click="handleFilterChange(filter.value)"
            >
              {{ filter.label }}
            </button>
          </div>

          <div class="map-sidebar__search">
            <el-input
              v-model="searchKeyword"
              placeholder="在结果中搜索"
              prefix-icon="el-icon-search"
              clearable
              @clear="handleSearch"
              @keyup.enter.native="handleSearch"
            />
          </div>

          <div
            ref="listRef"
            class="map-sidebar__list"
            @scroll="handleListScroll"
          >
            <div v-if="listLoading && !results.length" class="map-sidebar__status">
              加载中...
            </div>
            <div v-else-if="!results.length" class="map-sidebar__status">
              暂无数据
            </div>
            <div
              v-for="item in results"
              :key="item.id"
              class="map-sidebar__card"
            >
              <img
                :src="item.image || defaultCover"
                :alt="item.name"
                class="map-sidebar__card-img"
                :class="{ 'map-sidebar__card-img--default': !item.image }"
              />
              <div class="map-sidebar__card-body">
                <h4 class="map-sidebar__card-name">
                  {{ item.name }}
                  <span v-if="item.era" class="map-sidebar__card-era">{{ item.era }}</span>
                </h4>
                <p v-if="item.meta" class="map-sidebar__card-meta">{{ item.meta }}</p>
                <div class="map-sidebar__card-footer">
                  <span
                    class="map-sidebar__tag"
                    :class="`map-sidebar__tag--${item.type}`"
                  >
                    {{ item.tag }}
                  </span>
                  <router-link
                    v-if="item.type"
                    :to="`/knowledge/${item.type}/${item.id}`"
                    class="map-sidebar__detail"
                  >
                    查看详情 &gt;
                  </router-link>
                </div>
              </div>
            </div>
            <div v-if="loadingMore" class="map-sidebar__status map-sidebar__status--more">
              加载更多...
            </div>
            <div
              v-else-if="results.length && !hasMore"
              class="map-sidebar__status map-sidebar__status--more"
            >
              已加载全部
            </div>
          </div>
        </div>
      </aside>
    </div>
  </section>
</template>

<script>
import {
  searchKnowledgeEntities,
  fetchKnowledgeFacets,
  mapKnowledgeItem,
  mapIdentityFacets
} from '@/api/knowledge'
import { ontologyTypes as defaultOntologyTypes } from '@/views/knowledge/data.js'
import defaultCover from '@/assets/default-cover-square.png'

const TIANDITU_TK = '012930268d06a0daf66cafd7d2c0f45e'
const CHINA_CENTER = { lng: 104.195397, lat: 35.86166 }
const CHINA_ZOOM = 4
/** 选中省份后的最大缩放，避免 setViewport 贴边放得过大 */
const PROVINCE_MAX_ZOOM = 6
const PROVINCE_ZOOM_OUT = 1
const PROVINCE_STYLE = {
  color: '#8b7355',
  weight: 1,
  opacity: 0.95,
  fillColor: '#c4b498',
  fillOpacity: 0.28
}
const PROVINCE_ACTIVE_STYLE = {
  color: '#556b2f',
  weight: 1.5,
  opacity: 1,
  fillColor: '#6b8e4e',
  fillOpacity: 0.5
}

const DEFAULT_FILTERS = [
  { label: '全部', value: 'all', id: null },
  ...defaultOntologyTypes.map((item) => ({
    label: item.label,
    value: item.key,
    id: item.id || null
  }))
]

export default {
  name: 'KnowledgeMapSection',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      mapInstance: null,
      provinceOverlays: [],
      selectedProvince: '全国',
      mapMode: 'province',
      activeFilter: 'all',
      searchKeyword: '',
      searchTimer: null,
      page: 1,
      pageSize: 12,
      total: 0,
      results: [],
      listLoading: false,
      loadingMore: false,
      identityTypes: defaultOntologyTypes,
      stats: defaultOntologyTypes.map((item) => ({
        label: item.label,
        value: formatCount(item.count)
      })),
      mapModes: [
        { label: '地区地图', value: 'region' },
        { label: '省级地图', value: 'province' },
        { label: '市级地图', value: 'city' }
      ],
      filters: DEFAULT_FILTERS,
      defaultCover
    }
  },
  computed: {
    hasMore() {
      return this.results.length < this.total
    }
  },
  watch: {
    visible(val) {
      if (val && this.mapInstance) {
        setTimeout(() => this.handleResize(), 100)
      }
    },
    searchKeyword() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.refreshData()
      }, 350)
    }
  },
  mounted() {
    this.initMap()
    this.refreshData()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
    window.removeEventListener('resize', this.handleResize)
    this.destroyMap()
  },
  methods: {
    delay(seconds) {
      return {
        transitionDelay: this.visible ? `${seconds}s` : '0s'
      }
    },
    buildQueryParams() {
      const identityId = this.resolveIdentityId()
      const params = {
        identityId,
        keyword: this.searchKeyword.trim() || undefined
      }
      if (this.selectedProvince && this.selectedProvince !== '全国') {
        params.province = this.selectedProvince
      }
      return params
    },
    resolveIdentityId() {
      if (this.activeFilter === 'all') {
        return undefined
      }
      const matched = this.filters.find((item) => item.value === this.activeFilter)
      if (matched && matched.id) {
        return matched.id
      }
      const type = this.identityTypes.find((item) => item.key === this.activeFilter)
      return type && type.id ? type.id : undefined
    },
    mapCardItem(item) {
      const eraField = (item.meta || []).find((field) =>
        /生卒|时间|年代|时期/.test(field.label || '')
      )
      const metaText = (item.meta || [])
        .filter((field) => field !== eraField)
        .slice(0, 2)
        .map((field) => `${field.value}`)
        .filter(Boolean)
        .join(' · ')

      return {
        id: item.id,
        name: item.title,
        type: item.type || 'person',
        era: eraField && eraField.value ? `（${eraField.value}）` : '',
        meta: metaText || item.intro || '',
        tag: item.typeLabel || '实体',
        image: item.image || ''
      }
    },
    updateFiltersFromFacets(identities) {
      const mapped = mapIdentityFacets(identities, defaultOntologyTypes)
      this.identityTypes = mapped
      this.filters = [
        { label: '全部', value: 'all', id: null },
        ...mapped.map((item) => ({
          label: item.label,
          value: item.key,
          id: item.id || null
        }))
      ]
      this.stats = mapped.map((item) => ({
        label: item.label,
        value: formatCount(item.count)
      }))
    },
    async fetchFacets() {
      const facets = await fetchKnowledgeFacets(this.buildQueryParams())
      this.updateFiltersFromFacets(facets.identities)
    },
    async fetchList(append = false) {
      if (append) {
        if (this.loadingMore || this.listLoading || !this.hasMore) {
          return
        }
        this.loadingMore = true
      } else {
        this.listLoading = true
      }

      const nextPage = append ? this.page + 1 : 1
      try {
        const pageRes = await searchKnowledgeEntities({
          ...this.buildQueryParams(),
          page: nextPage,
          limit: this.pageSize
        })
        const mapped = (pageRes.records || [])
          .map(mapKnowledgeItem)
          .map((item) => this.mapCardItem(item))

        this.total = pageRes.total || 0
        if (append) {
          this.results = [...this.results, ...mapped]
          this.page = nextPage
        } else {
          this.results = mapped
          this.page = 1
        }
      } catch (error) {
        if (!append) {
          this.results = []
          this.total = 0
        }
      } finally {
        this.listLoading = false
        this.loadingMore = false
      }
    },
    async refreshData() {
      await Promise.all([
        this.fetchFacets().catch(() => {}),
        this.fetchList(false)
      ])
    },
    handleFilterChange(value) {
      if (this.activeFilter === value) {
        return
      }
      this.activeFilter = value
      this.refreshData()
    },
    handleSearch() {
      clearTimeout(this.searchTimer)
      this.refreshData()
    },
    handleListScroll() {
      const el = this.$refs.listRef
      if (!el || this.listLoading || this.loadingMore || !this.hasMore) {
        return
      }
      const threshold = 48
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
        this.fetchList(true)
      }
    },
    restoreNational() {
      this.selectedProvince = '全国'
      this.updateProvinceHighlight(null)
      this.resetMapView()
    },
    handleReset() {
      if (this.selectedProvince === '全国') {
        return
      }
      this.restoreNational()
      this.refreshData()
      this.$nextTick(() => {
        const el = this.$refs.listRef
        if (el) {
          el.scrollTop = 0
        }
      })
    },
    loadTiandituApi() {
      if (typeof window !== 'undefined' && window.T && window.T.Map) {
        return Promise.resolve()
      }
      return new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-tianditu-api]')
        if (existing) {
          existing.addEventListener('load', () => resolve())
          existing.addEventListener('error', reject)
          return
        }
        const script = document.createElement('script')
        script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${TIANDITU_TK}`
        script.async = true
        script.dataset.tiandituApi = '1'
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('天地图脚本加载失败'))
        document.head.appendChild(script)
      })
    },
    async initMap() {
      if (!this.$refs.chartRef) return

      try {
        await this.loadTiandituApi()
      } catch (error) {
        console.error('[KnowledgeMapSection] 天地图加载失败:', error)
        return
      }

      const T = window.T
      if (!T || !T.Map) {
        console.error('[KnowledgeMapSection] 天地图 API 不可用')
        return
      }

      this.destroyMap()
      this.mapInstance = new T.Map(this.$refs.chartRef, {
        projection: 'EPSG:4326'
      })
      this.mapInstance.centerAndZoom(
        new T.LngLat(CHINA_CENTER.lng, CHINA_CENTER.lat),
        CHINA_ZOOM
      )
      this.mapInstance.enableScrollWheelZoom()
      this.mapInstance.setMinZoom(3)
      this.mapInstance.setMaxZoom(12)

      await this.loadProvinceOverlays()
      this.$nextTick(() => {
        this.handleResize()
        // 布局完成后再校一次尺寸，避免首屏高度为 0 导致瓦片/版权控件显示不全
        setTimeout(() => this.handleResize(), 200)
      })
    },
    async loadProvinceOverlays() {
      const T = window.T
      if (!T || !this.mapInstance) return

      let chinaJson = { type: 'FeatureCollection', features: [] }
      try {
        const response = await fetch(`${process.env.BASE_URL}geo/china.json`)
        if (!response.ok) {
          throw new Error(`省份边界加载失败 (${response.status})`)
        }
        chinaJson = await response.json()
      } catch (error) {
        console.error('[KnowledgeMapSection] 省份边界加载失败:', error)
        return
      }

      this.provinceOverlays = []
      ;(chinaJson.features || []).forEach((feature) => {
        const name = feature && feature.properties && feature.properties.name
        if (!name || name === '九段线') return

        const geometry = feature.geometry || {}
        const polygons = geometry.type === 'Polygon'
          ? [geometry.coordinates]
          : (geometry.type === 'MultiPolygon' ? geometry.coordinates : [])

        polygons.forEach((polygon) => {
          const ring = polygon && polygon[0]
          if (!Array.isArray(ring) || !ring.length) return
          const path = ring
            .map((point) => {
              const lng = Number(point[0])
              const lat = Number(point[1])
              if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
              return new T.LngLat(lng, lat)
            })
            .filter(Boolean)
          if (path.length < 3) return

          const overlay = new T.Polygon(path, { ...PROVINCE_STYLE })
          overlay.addEventListener('click', () => {
            this.selectProvince(name)
          })
          this.mapInstance.addOverLay(overlay)
          this.provinceOverlays.push({ name, overlay, path })
        })
      })
    },
    selectProvince(name) {
      if (!name) return
      this.selectedProvince = name
      this.updateProvinceHighlight(name)
      this.focusProvince(name)
      this.refreshData()
      this.$nextTick(() => {
        const el = this.$refs.listRef
        if (el) {
          el.scrollTop = 0
        }
      })
    },
    updateProvinceHighlight(name) {
      this.provinceOverlays.forEach((item) => {
        const active = !!name && item.name === name
        const style = active ? PROVINCE_ACTIVE_STYLE : PROVINCE_STYLE
        const overlay = item.overlay
        if (!overlay) return
        if (typeof overlay.setColor === 'function') overlay.setColor(style.color)
        if (typeof overlay.setWeight === 'function') overlay.setWeight(style.weight)
        if (typeof overlay.setOpacity === 'function') overlay.setOpacity(style.opacity)
        if (typeof overlay.setFillColor === 'function') overlay.setFillColor(style.fillColor)
        if (typeof overlay.setFillOpacity === 'function') overlay.setFillOpacity(style.fillOpacity)
      })
    },
    focusProvince(name) {
      const T = window.T
      if (!T || !this.mapInstance || !name) return
      const points = this.provinceOverlays
        .filter((item) => item.name === name)
        .reduce((list, item) => list.concat(item.path || []), [])
      if (!points.length) return

      if (typeof this.mapInstance.setViewport === 'function') {
        this.mapInstance.setViewport(points)
      } else {
        this.mapInstance.centerAndZoom(points[0], PROVINCE_MAX_ZOOM)
        return
      }

      // 在自适应视野基础上再缩小一级，并限制最大倍数
      const zoom = typeof this.mapInstance.getZoom === 'function'
        ? this.mapInstance.getZoom()
        : null
      if (!Number.isFinite(zoom) || typeof this.mapInstance.setZoom !== 'function') {
        return
      }
      const nextZoom = Math.min(
        Math.max(zoom - PROVINCE_ZOOM_OUT, CHINA_ZOOM + 1),
        PROVINCE_MAX_ZOOM
      )
      if (nextZoom !== zoom) {
        this.mapInstance.setZoom(nextZoom)
      }
    },
    resetMapView() {
      const T = window.T
      if (!T || !this.mapInstance) return
      this.mapInstance.centerAndZoom(
        new T.LngLat(CHINA_CENTER.lng, CHINA_CENTER.lat),
        CHINA_ZOOM
      )
    },
    destroyMap() {
      if (this.mapInstance && typeof this.mapInstance.clearOverLays === 'function') {
        this.mapInstance.clearOverLays()
      }
      this.provinceOverlays = []
      this.mapInstance = null
      if (this.$refs.chartRef) {
        this.$refs.chartRef.innerHTML = ''
      }
    },
    handleResize() {
      if (this.mapInstance && typeof this.mapInstance.checkResize === 'function') {
        this.mapInstance.checkResize()
      }
    }
  }
}

function formatCount(value) {
  const count = Number(value) || 0
  if (count >= 10000) {
    const wan = count / 10000
    return `${wan >= 10 ? Math.round(wan) : wan.toFixed(1).replace(/\.0$/, '')}万`
  }
  return count.toLocaleString()
}
</script>

<style lang="scss" scoped>
.map-section {
  padding: $spacing-4xl 0;
  background:
    url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"),
    $color-paper-light;

  &__layout {
    display: flex;
    gap: $spacing-xl;
    @include page-container;
    align-items: stretch;
    // Cap map + list to viewport; sidebar list scrolls inside
    height: min(#{rem(580)}, calc(100vh - #{rem(180)}));
    max-height: min(#{rem(580)}, calc(100vh - #{rem(180)}));
  }

  &__main {
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: $spacing-xl;
    margin-bottom: $spacing-xl;
    flex-wrap: wrap;
    flex-shrink: 0;
  }

  &__title {
    font-family: $font-serif;
    font-size: clamp(#{rem(22)}, 2.5vw, #{rem(32)});
    font-weight: 700;
    color: $color-brown-dark;
    letter-spacing: 2px;
  }

  &__subtitle {
    margin-top: $spacing-xs;
    font-size: rem(11);
    letter-spacing: 3px;
    color: $color-text-light;
  }

  &__stats {
    display: flex;
    flex-wrap: nowrap;
    gap: $spacing-lg;
    max-width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
  }

  &__stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
    padding: 0 $spacing-base;
    border-left: 1px solid $color-border;
    white-space: nowrap;

    &:first-child {
      border-left: none;
      padding-left: 0;
    }
  }

  &__stat-label {
    font-size: rem(12);
    color: $color-text-light;
  }

  &__stat-value {
    font-family: $font-serif;
    font-size: rem(18);
    font-weight: 600;
    color: $color-brown-dark;
    margin-top: $spacing-xs;
  }

  &__chart {
    position: relative;
    flex: 1;
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;
    background: rgba($color-cream, 0.5);
    border-radius: 4px;
    padding: $spacing-sm;
    // 勿用 overflow:hidden，否则会裁掉底部署名与天地图版权
    overflow: visible;
  }

  &__chart-frame {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    border-radius: 2px;
    overflow: hidden;
    background: #f3f1ea;
  }

  &__chart-canvas {
    width: 100%;
    height: 100%;
    min-height: rem(240);
    position: relative;

    ::v-deep .tdt-control-copyright,
    ::v-deep .tdt-control,
    ::v-deep .tdt-control-container {
      z-index: 500 !important;
      font-size: 11px !important;
    }

    ::v-deep .tdt-control-copyright {
      bottom: 4px !important;
      left: 4px !important;
      max-width: calc(100% - 12px);
      white-space: normal !important;
      line-height: 1.35;
      background: rgba(255, 255, 255, 0.88);
      padding: 2px 6px;
      border-radius: 2px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
  }

  &__map-credit {
    flex: 0 0 auto;
    margin: 0;
    font-size: rem(11);
    color: $color-text-light;
    text-align: right;
    letter-spacing: 0.5px;
    line-height: 1.4;
  }

  &__sidebar {
    width: rem(320);
    max-width: 100%;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-self: stretch;
    min-height: 0;
    max-height: 100%;
  }
}

.map-sidebar {
  background: rgba($color-cream, 0.95);
  border-radius: 4px;
  padding: $spacing-base;
  box-shadow: 0 4px 20px rgba($color-brown-dark, 0.08);
  flex: 1 1 auto;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__modes {
    margin-bottom: $spacing-base;
  }

  &__modes-label {
    display: block;
    font-size: rem(12);
    color: $color-text-light;
    margin-bottom: $spacing-sm;
  }

  &__mode-tabs {
    display: flex;
    gap: $spacing-xs;
  }

  &__mode-tab {
    flex: 1;
    padding: $spacing-sm;
    font-size: rem(12);
    border-radius: 4px;
    background: $color-paper-light;
    color: $color-text;
    transition: all 0.2s;

    &--active {
      background: $color-brown-dark;
      color: $color-white;
    }
  }

  &__context {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-sm;
    font-size: rem(13);
    margin-bottom: $spacing-base;
    padding-bottom: $spacing-sm;
    border-bottom: 1px solid $color-border;
  }

  &__reset {
    flex-shrink: 0;
    padding: 2px $spacing-sm;
    font-size: rem(12);
    color: $color-text-light;
    border: 1px solid $color-border;
    border-radius: 4px;
    background: $color-white;
    transition: color 0.2s, border-color 0.2s, background-color 0.2s;

    &:hover:not(:disabled) {
      color: $color-brown-dark;
      border-color: $color-brown-dark;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__filters {
    display: flex;
    flex-wrap: nowrap;
    gap: $spacing-xs;
    margin-bottom: $spacing-base;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
  }

  &__filter {
    flex: 0 0 auto;
    padding: $spacing-xs $spacing-sm;
    font-size: rem(12);
    border-radius: 4px;
    background: transparent;
    color: $color-text-light;
    transition: all 0.2s;
    white-space: nowrap;

    &--active {
      background: $color-brown-dark;
      color: $color-white;
    }
  }

  &__search {
    margin-bottom: $spacing-base;

    ::v-deep .el-input__inner {
      background: $color-white;
    }
  }

  &__list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: $spacing-sm;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
  }

  &__status {
    padding: $spacing-lg 0;
    text-align: center;
    font-size: rem(13);
    color: $color-text-light;

    &--more {
      padding: $spacing-sm 0 $spacing-base;
    }
  }

  &__card {
    display: flex;
    gap: $spacing-sm;
    padding: $spacing-sm;
    background: $color-white;
    border-radius: 4px;
    transition: box-shadow 0.2s;
    flex-shrink: 0;

    &:hover {
      box-shadow: 0 2px 8px rgba($color-brown-dark, 0.1);
    }
  }

  &__card-img {
    width: rem(56);
    height: rem(56);
    object-fit: cover;
    border-radius: 2px;
    flex-shrink: 0;
    filter: grayscale(0.6);

    &--default {
      filter: none;
    }
  }

  &__card-body {
    flex: 1;
    min-width: 0;
  }

  &__card-name {
    font-size: rem(14);
    font-weight: 600;
    color: $color-brown-dark;
    line-height: 1.3;
  }

  &__card-era {
    font-weight: 400;
    font-size: rem(12);
    color: $color-text-light;
  }

  &__card-meta {
    font-size: rem(12);
    color: $color-text-light;
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: $spacing-sm;
  }

  &__tag {
    font-size: rem(11);
    padding: 1px $spacing-sm;
    border-radius: 2px;

    &--person,
    &--renwu {
      background: #dbeafe;
      color: #1d4ed8;
    }

    &--org,
    &--jigou {
      background: #fef3c7;
      color: #b45309;
    }

    &--event,
    &--shijian {
      background: #dcfce7;
      color: #15803d;
    }

    &--location,
    &--didian {
      background: #ede9fe;
      color: #6d28d9;
    }
  }

  &__detail {
    font-size: rem(12);
    color: $color-text-light;

    &:hover {
      color: $color-brown-dark;
    }
  }
}

@media (max-width: 960px) {
  .map-section {
    &__layout {
      flex-direction: column;
      height: auto;
      max-height: none;
    }

    &__sidebar {
      width: 100%;
      max-height: none;
    }

    &__chart {
      min-height: rem(320);
    }

    &__chart-frame,
    &__chart-canvas {
      min-height: rem(320);
    }
  }

  .map-sidebar {
    max-height: none;
    overflow: visible;

    &__list {
      max-height: rem(360);
      overflow-y: auto;
    }
  }
}
</style>
